import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import { Form, FormSection } from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';

import type { FormApi, FormState } from 'informed';

interface Props<T> {
  callAfterSubmitSuccess?: () => Promise<void> | void;
  children: (formState: FormState<T>) => React.ReactNode;
  initialValues: T;
  padding?: number;
  setFormApi?: (formApi: FormApi<T>) => void;
  showUnsavedChangesWarning?: boolean;
  title?: string;
  updateValuesBeforeSubmit?: (values: T) => Promise<T> | T;
}

const SiteSettingsForm = <T extends any>({
  callAfterSubmitSuccess,
  children,
  initialValues,
  padding = 15,
  setFormApi,
  showUnsavedChangesWarning,
  title,
  updateValuesBeforeSubmit
}: Props<T>) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const [loading, setLoading] = useState(false);

  const submitForm = async (values: T) => {
    try {
      setLoading(true);

      if (updateValuesBeforeSubmit) {
        values = await updateValuesBeforeSubmit(values);
      }

      await api.put(`/admin/server-config`, { data: values });
      const response = await api.get('/admin/server-config');
      dispatch(actions.setServerConfig(response.data.data));
      dispatch(
        actions.addNotice({
          text: messages.settingsSavedSuccessfully,
          type: 'success'
        })
      );

      if (showUnsavedChangesWarning) {
        dispatch.updatePageState({
          hasUnsavedChanges: false,
          refreshInitialState: true
        });
      }

      if (callAfterSubmitSuccess) await callAfterSubmitSuccess();
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-form">
      <Box padding={0} title={title}>
        <Form<T>
          allowEmptyStrings={true}
          getApi={setFormApi}
          initialValues={initialValues}
          onSubmit={submitForm}
        >
          {({ formState }) => (
            <div>
              <div style={{ padding }}>
                {showUnsavedChangesWarning ? (
                  <UnsavedFormChanges initialValues={initialValues} />
                ) : null}

                {children(formState)}
              </div>

              <FormSection>
                <Button
                  category="success"
                  disabled={formState.invalid}
                  loading={loading}
                  text={messages.commonSaveChanges}
                  type="submit"
                />
              </FormSection>
            </div>
          )}
        </Form>
      </Box>
    </div>
  );
};

export default SiteSettingsForm;
